import styled from 'styled-components';

import { CloseIcon } from '@genially/icons';

import { SmallParagraph } from '../../../../../../../foundations/typefaces/SmallParagraph';
import style from '../../../../../../../style';
import type { Color } from '../../../../../../../style/types/theme';
import { Status } from '../../types';

interface StatusProps {
  $status: string;
}

interface TextProps extends StatusProps {
  $focused?: boolean;
}
interface CloseIconProps extends TextProps {
  $disabled?: boolean;
}

const getCursorStyle = (disabled: boolean, focused: boolean) => {
  if (disabled) {
    return 'not-allowed';
  }

  if (focused) {
    return 'pointer';
  }

  return 'default';
};

const isSuccessStatus = (status: string) => status === Status.SUCCESS;

const textColor = ($status: string, color: Color) =>
  isSuccessStatus($status)
    ? color.content.secondary.default()
    : color.content.error.default();

const hoverColor = ($status: string, $focused: boolean, color: Color) => {
  if ($focused) {
    return isSuccessStatus($status)
      ? color.content.secondary.hover()
      : color.content.error.default();
  }

  return textColor($status, color);
};

export const StyledCloseIcon = styled(CloseIcon)<CloseIconProps>(
  ({ theme: { color, size }, $status, $focused = false, $disabled = false }) => ({
    fill: textColor($status, color),
    width: size.units(1.5),
    height: size.units(1.5),
    cursor: getCursorStyle($disabled, $focused),

    ...style.mixins.devices.desktop({
      '&:hover': {
        fill: hoverColor($status, $focused, color),
      },
    }),
  }),
);

export const TextWrapper = styled(SmallParagraph)<TextProps>(
  ({ theme: { color, size }, $status, $focused = false }) => ({
    color: textColor($status, color),
    maxWidth: size.units(20),
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    paddingRight: size.units(1.5),

    ...style.mixins.devices.desktop({
      '&:hover': {
        color: hoverColor($status, $focused, color),
      },
    }),
  }),
);

export const Wrapper = styled.div<StatusProps>(
  ({ theme: { size, color, borderRadius }, $status }) => ({
    height: size.units(3),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `0 ${size.units(0.5)} 0 ${size.units(1.25)}`,
    borderRadius: borderRadius.small,

    border: isSuccessStatus($status)
      ? '1px solid transparent'
      : `1px solid ${color.border.error.default()}`,

    background: isSuccessStatus($status)
      ? color.background.quaternary.default()
      : 'transparent',

    ':focus-visible': {
      outline: 'auto -webkit-focus-ring-color',
    },
  }),
);

export const UserAvatarWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingRight: size.units(0.5),
}));
